import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { suggestBusinesses } from "@/services/publicApi";
import { businessPath } from "@/utils/slug";
import { COVERED_REGIONS } from "@/config/regions";
import type { BusinessSuggestion, Category, SubcategoryOption } from "@/types";

export interface DirectoryFilterState {
  q: string;
  category: string;
  subcategory: string;
  district: string;
  town: string;
  sort: string;
  openNow: boolean;
  minRating: number;
}

interface DirectoryFiltersProps {
  categories: Category[];
  subcategories: SubcategoryOption[];
  state: DirectoryFilterState;
  onChange: (patch: Partial<DirectoryFilterState>) => void;
  onReset: () => void;
}

export default function DirectoryFilters({
  categories,
  subcategories,
  state,
  onChange,
  onReset,
}: DirectoryFiltersProps) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<BusinessSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const blurTimer = useRef<number | null>(null);

  const ratings = [
    { value: 0, label: "Any rating" },
    { value: 4, label: "4.0 & above" },
    { value: 4.5, label: "4.5 & above" },
  ];

  const selectedCategory = categories.find((c) => c.slug === state.category || c.id === state.category);
  const selectedDistrict = COVERED_REGIONS.find((r) => r.slug === state.district) ?? null;

  useEffect(() => {
    const query = state.q.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      suggestBusinesses(query, 6)
        .then((res) => {
          if (cancelled) return;
          setSuggestions(res);
          setShowSuggestions(res.length > 0);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [state.q]);

  const openSuggestion = (suggestion: BusinessSuggestion) => {
    setShowSuggestions(false);
    navigate(businessPath({ slug: suggestion.slug, locationName: suggestion.locationName }));
  };

  return (
    <aside className="lg:sticky lg:top-24 h-fit rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between border-b border-background-100 px-4 py-3">
        <h2 className="inline-flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
          <i className="ri-equalizer-line text-primary-600"></i> Filters
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-xs font-semibold text-secondary-700 hover:text-secondary-900 cursor-pointer"
        >
          Clear all
        </button>
      </div>

      <div className="space-y-5 p-4">
        <div>
          <label htmlFor="f-keyword" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Keyword
          </label>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              id="f-keyword"
              value={state.q}
              onChange={(e) => onChange({ q: e.target.value })}
              onFocus={() => setShowSuggestions(suggestions.length > 0)}
              onBlur={() => {
                blurTimer.current = window.setTimeout(() => setShowSuggestions(false), 150);
              }}
              placeholder="Business name or service"
              autoComplete="off"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none"
            />
            {showSuggestions && (
              <ul className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border border-background-200 bg-background-50 ring-1 ring-background-100">
                {suggestions.map((s) => (
                  <li key={`${s.slug}-${s.locationName}`}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        if (blurTimer.current) window.clearTimeout(blurTimer.current);
                        openSuggestion(s);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-background-100 cursor-pointer"
                    >
                      <i className="ri-store-3-line text-secondary-500"></i>
                      <span className="flex-1 truncate">
                        <span className="font-medium text-foreground-900">{s.name}</span>
                        <span className="block text-xs text-foreground-500">
                          {s.categoryName} · {s.locationName}
                        </span>
                      </span>
                      <i className="ri-arrow-right-up-line text-foreground-400"></i>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="f-category" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Category
          </label>
          <select
            id="f-category"
            value={state.category}
            onChange={(e) => onChange({ category: e.target.value, subcategory: "" })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {selectedCategory && subcategories.length > 0 && (
          <div>
            <label htmlFor="f-subcategory" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
              Subcategory
            </label>
            <select
              id="f-subcategory"
              value={state.subcategory}
              onChange={(e) => onChange({ subcategory: e.target.value })}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
            >
              <option value="">All subcategories</option>
              {subcategories.map((s) => (
                <option key={s.id} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="f-district" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
            District
          </label>
          <select
            id="f-district"
            value={state.district}
            onChange={(e) => onChange({ district: e.target.value, town: "" })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            <option value="">All districts</option>
            {COVERED_REGIONS.map((region) => (
              <option key={region.slug} value={region.slug}>
                {region.district}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="f-taluk" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500">
            Taluk
          </label>
          <select
            id="f-taluk"
            value={state.town}
            onChange={(e) => onChange({ town: e.target.value })}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            <option value="">All taluks</option>
            {selectedDistrict
              ? selectedDistrict.taluks.map((taluk) => (
                  <option key={taluk.slug} value={taluk.slug}>
                    {taluk.name}
                  </option>
                ))
              : COVERED_REGIONS.map((region) => (
                  <optgroup key={region.slug} label={region.district}>
                    {region.taluks.map((taluk) => (
                      <option key={taluk.slug} value={taluk.slug}>
                        {taluk.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
          </select>
          <p className="mt-1.5 text-[11px] leading-snug text-foreground-500">
            {selectedDistrict
              ? `Showing taluks in ${selectedDistrict.district}`
              : "Pick a district to narrow the taluk list."}
          </p>
        </div>

        <div>
          <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-foreground-500">Minimum rating</span>
          <div className="space-y-1.5">
            {ratings.map((r) => (
              <label key={r.value} className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
                <input
                  type="radio"
                  name="minRating"
                  checked={state.minRating === r.value}
                  onChange={() => onChange({ minRating: r.value })}
                  className="h-4 w-4 accent-primary-500 cursor-pointer"
                />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-background-100 pt-4">
          <label className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-foreground-700">
            <span className="inline-flex items-center gap-2">
              <i className="ri-time-line text-accent-600"></i> Open now only
            </span>
            <input
              type="checkbox"
              checked={state.openNow}
              onChange={(e) => onChange({ openNow: e.target.checked })}
              className="h-4 w-4 accent-accent-500 cursor-pointer"
            />
          </label>
        </div>
      </div>
    </aside>
  );
}